import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isSessionValid } from '@app/auth/helpers/session-validity';
import { LoginModel, RegisterModel } from '@app/auth/models';
import { Store } from '@app/store';

import { ApiUrlService } from '@core/services/common/api-url.service';

import { User, UserInterface } from '@modules/ecommerce/shared/models';
import { LocalStorageService, SessionStorageService } from '@shared/services/storage';

import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';

export interface Token {
  accessToken: string;
  expiresAt: string;
}

/**
 * Ścieżka odnowienia sesji sklepu, liczona od adresu zwracanego przez `ApiUrlService`.
 *
 * MUSI stać pod prefiksem `client/`. Poza localhostem adres API to BIEŻĄCA domena, czyli
 * domena galerii, a ta przekazuje do backendu wyłącznie `client/*` — żądanie na `/api/*`
 * trafia w serwer SSR macierzy i dostaje stamtąd 404, nie docierając do Laravela. Objaw
 * jest niemy: błąd odnowienia podjętego „na zapas" jest świadomie połykany, więc sesja po
 * prostu kończyła się po dobie, niezależnie od tego, jak często klient wracał do sklepu.
 *
 * Stała, a nie dwa napisy: ten sam adres czyta strażnik pętli w interceptorze
 * uwierzytelniania. Gdy były przepisane ręcznie, zmiana jednego z nich nie wywalała się
 * głośno — odmowa 401 z samego odnowienia przestawała być rozpoznawana i uruchamiała
 * kolejne odnowienie.
 */
export const SHOP_RENEWAL_PATH = 'client/renew';

/**
 * Odpowiedź odnowienia sesji. Pola w notacji z podkreśleniami, bo żądanie odnawiania prosi
 * o pominięcie konwersji nazw — inaczej token zapisałby się jako niezdefiniowany.
 */
export interface RenewedToken {
  access_token: string;
  expires_at: string;
}

/**
 * Wycinek Web Locks API, z którego korzystamy — tyle, ile potrzeba do ustawienia kart
 * w kolejce. Opisany tutaj, bo konfiguracja TypeScriptu nie musi mieć go w bibliotece
 * standardowej.
 */
interface CrossTabLockManager {
  request(name: string, callback: () => Promise<void>): Promise<void>;
}

@Injectable()
export class AuthService {
  /**
   * Nazwa blokady międzykartowej. Jedna na origin, więc wszystkie karty sklepu stają
   * w tej samej kolejce.
   */
  private static readonly RENEWAL_LOCK = 'artsaas-shop-session-renewal';

  public state = new BehaviorSubject<Token | null | undefined>(undefined);
  public authState$ = this.state.asObservable();

  /** Strumień trwającego odnowienia — kto zapyta drugi, wisi na tej samej odpowiedzi. */
  private renewal$: Observable<RenewedToken> | null = null;

  user$ = this.authState$.pipe(
    switchMap((token: Token) =>
      token ? this.current(token.accessToken) : of(null),
    ),
    tap((user: User | null) => this.store.set('user', user)),
    share(),
  );

  constructor(
    private store: Store,
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private localStorageService: LocalStorageService,
    private storageService: SessionStorageService,
  ) {
  }

  register(path: string, register: RegisterModel) {
    const url = this.apiUrlService.getApiUrl() + path;
    const locale = this.storageService.getItem('language');
    return this.httpClient.post(url, {
      ...register,
      password_confirm: register.password,
      lang: locale,
    });
  }

  passwordReset(path: string, resetData: any) {
    const url = this.apiUrlService.getApiUrl() + path;
    return this.httpClient.post(url, resetData).pipe(
      map((x) => true),
      catchError((err) => of(err.ok)),
    );
  }

  login(path: string, login: LoginModel) {
    const url = this.apiUrlService.getApiUrl() + path;
    return this.httpClient
      .post(url, login, {
        headers: new HttpHeaders({'Content-Type': 'application/json'}),
      })
      .pipe(
        catchError((failure) => {
          // Formularz czyta z odrzuconej wartości `error.errors`. Dla każdego statusu poza 400
          // leciała tu surowa odpowiedź błędu, w której tego pola nie ma — pętla po błędach nie
          // wykonywała się ani razu i klient nie widział ŻADNEGO komunikatu. Cisza jest gorsza
          // od złego komunikatu: nie da się z niej wyczytać, czy hasło było błędne, czy po
          // prostu trzeba odczekać.
          const key = AuthService.loginErrorKey(failure.status);

          if (key) {
            const errors = {
              password: [key],
              email: [key],
            };

            return throwError({ok: false, error: {errors}});
          }

          return throwError(failure);
        }),
        tap((response: Token) => {
          if (response && response.accessToken) {
            this.storeSession(response.accessToken, response.expiresAt);
          }
        }),
      );
  }

  /**
   * Klucz komunikatu pod polami formularza logowania, dobrany po rodzaju odmowy.
   *
   * Komunikat o błędnych danych zostaje JEDNAKOWY dla złego hasła i nieistniejącego konta —
   * inaczej sam formularz zdradzałby, czy konto o podanym adresie u nas istnieje. Sklep odmawia
   * poświadczeń kodem 400, nie 401 jak panel; to udokumentowana różnica kontraktu obu
   * publiczności, nie pomyłka.
   *
   * Zwrócone `null` znaczy „nie umiemy tego nazwać" — wtedy dalej leci surowy błąd, tak jak
   * dotąd, zamiast podstawiania komunikatu, który mógłby wprowadzić w błąd.
   */
  private static loginErrorKey(status: number): string | null {
    // PEŁNA ścieżka klucza, z sekcją. Szablon pola formularza tłumaczy to, co dostanie, bez
    // doklejania czegokolwiek — a teksty logowania mieszkają w sekcji `forms`. Gołe `errorLogin`
    // nie rozwiązywało się więc w ogóle i klient widział pod polem DOSŁOWNY napis „errorLogin".
    // Pozostałe błędy pola (`forms.required`, `forms.email`) niosą prefiks od zawsze; ten jeden
    // go nie miał.
    if (status === 400) {
      return 'forms.errorLogin';
    }

    if (status === 429) {
      return 'forms.errorLoginTooManyAttempts';
    }

    if (status >= 500) {
      return 'forms.errorLoginServerError';
    }

    // Status 0 to żądanie, które nie doszło: brak sieci, nieosiągalne API, odrzucone zapytanie
    // wstępne. Nie wolno tego ogłaszać jako błędnych danych — hasło mogło być poprawne — ani
    // zostawiać bez komunikatu, bo formularz milczy i klient nie wie, co się stało.
    if (status === 0) {
      return 'forms.errorLoginConnection';
    }

    return null;
  }

  logout(path = 'client/logout') {
    const url = this.apiUrlService.getApiUrl() + path;
    const accessToken = this.localStorageService.getItem('token') || '';

    // Bez poświadczenia nie ma czego odwoływać po stronie serwera — żądanie wróciłoby
    // kodem 401 i zostawiło wpis w logach. Ta sama straż stoi w panelu klienta.
    if (!accessToken) {
      this.clearSession();

      return of([]);
    }

    // Ciało jest puste celowo. Token to poświadczenie nagłówka, nie treść żądania, a gdy
    // szedł jako ciało, interceptor zamieniający nazwy pól na snake_case próbował iterować
    // po kluczach napisu i wywracał się na `key.startsWith is not a function`. Błąd łapał
    // catchError poniżej, więc strumień kończył się „powodzeniem", a żądanie nigdy nie
    // opuszczało przeglądarki — sesja po stronie serwera zostawała otwarta.
    const zadanie$ = this.httpClient
      .post(url, {}, {
        headers: new HttpHeaders({Authorization: 'Bearer ' + accessToken}),
      })
      .pipe(
        catchError(_ => of([])),
        // Rozbieranie sesji przechodzi jedną drogą — inaczej łatwo dołożyć miejsce,
        // które czyści magazyn i zapomina o stanie.
        finalize(() => this.clearSession()),
        shareReplay(1),
      );

    // Żądanie musi dojść do serwera niezależnie od tego, co odbiorca zrobi ze strumieniem.
    // Menu konta subskrybuje wylogowanie przez switchMap, a ten potrafi przerwać subskrypcję
    // w tej samej chwili, w której powstaje — wtedy żądanie nigdy nie opuszczało przeglądarki,
    // sesja po stronie serwera zostawała otwarta, a token działał dalej mimo wylogowania
    // w interfejsie. Własna subskrypcja utrzymuje je przy życiu, a shareReplay sprawia,
    // że odbiorca dostaje ten sam wynik i nie wysyła drugiego żądania.
    zadanie$.subscribe();

    return zadanie$;
  }

  autoLogin() {
    const accessToken = this.localStorageService.getItem('token');
    const expiresAt = this.localStorageService.getItem('expiresAt');

    // Pusty magazyn to nie wygaśnięcie: nie ma czego czyścić ani zerować,
    // stan zostaje nieustalony do pierwszego logowania.
    if (!accessToken || !expiresAt) {
      return;
    }

    // Do tej pory sprawdzana była wyłącznie obecność kluczy, więc wygasły token
    // wyglądał jak zalogowana sesja aż do pierwszego żądania z 401.
    // O ważność pytamy już odczytanymi wartościami — drugi odczyt tych samych
    // kluczy mógłby zobaczyć inny stan magazynu niż ten, który zaraz zapisujemy.
    if (!isSessionValid(accessToken, expiresAt)) {
      this.clearSession();
      return;
    }

    this.state.next({accessToken, expiresAt});
  }

  /**
   * Wspólna odpowiedź na pytanie „czy zapisana sesja jest jeszcze ważna”.
   * Pytają o to i automatyczne logowanie, i strażnik trasy konta — obie strony
   * muszą dostać ten sam wynik dla tych samych danych w magazynie.
   */
  hasValidSession(): boolean {
    return isSessionValid(
      this.localStorageService.getItem('token'),
      this.localStorageService.getItem('expiresAt'),
    );
  }

  /**
   * Zamknięcie sesji po stronie przeglądarki: magazyn i stan naraz.
   * Wyczyszczenie samego magazynu zostawiało w interfejsie zalogowanego
   * użytkownika aż do przeładowania strony.
   */
  clearSession(): void {
    this.clearLoginStorage();
    this.state.next(null);
  }

  /**
   * Zapis odnowionej sesji: magazyn i stan naraz — odwrotność zamknięcia sesji.
   * Zapisanie samego magazynu zostawiało w stanie usługi poprzedni token i jego
   * datę wygaśnięcia, więc każdy widok liczący czas do wygaśnięcia ze stanu
   * pracowałby na danych tokenu, który już nie obowiązuje.
   */
  storeRenewedSession(accessToken: string, expiresAt: string): void {
    this.storeSession(accessToken, expiresAt);
  }

  /**
   * Zapis sesji w jednym miejscu: magazyn, znacznik wydania i stan usługi naraz.
   *
   * Znacznik wydania jest tu nowy. Bez niego nie dało się policzyć, ile z okresu ważności już
   * minęło — a sklep nie miał od czego liczyć, więc nie odnawiał sesji nigdy. Stawia go zegar
   * PRZEGLĄDARKI, podczas gdy termin przychodzi z serwera, więc rozjazd zegarów przesuwa próg
   * odnowienia w obie strony.
   */
  private storeSession(accessToken: string, expiresAt: string): void {
    this.localStorageService.setItem('token', accessToken);
    this.localStorageService.setItem('expiresAt', expiresAt);
    this.localStorageService.setItem('issuedAt', new Date().toISOString());
    this.state.next({accessToken, expiresAt});
  }

  /**
   * Odnawia sesję, jeśli minęła połowa okresu ważności tokenu. Wołane przy starcie sklepu
   * i przy powrocie do karty.
   *
   * DLACZEGO W OGÓLE. Sklep nie przedłużał sesji klienta ANI RAZU: odnowienie było wyłącznie
   * reakcją na odmowę 401, a trasa odnawiania stoi za strażą uwierzytelnienia — żądanie
   * z wygasłym tokenem w ogóle do niej nie dociera. „Reakcja na 401" pokrywa więc wyłącznie
   * odmowy przy WAŻNYM tokenie (rotacja, karencja, niezmienność ewidencji), a nigdy
   * wygaśnięcie. Sesja kończyła się zawsze po swoim pierwotnym terminie, niezależnie od tego,
   * jak często klient wracał do sklepu.
   *
   * Zwraca informację, czy odnowienie zostało podjęte, żeby wołający nie powtarzał warunku.
   */
  renewIfHalfWindowPassed(): boolean {
    if (!this.localStorageService.getItem('token') || !this.isOlderThanHalfWindow()) {
      return false;
    }

    void this.renewUnderCrossTabLock();

    return true;
  }

  /**
   * Wspólny strumień odnowienia — ten sam, z którego korzysta interceptor przy odmowie 401.
   * Jeden właściciel, więc odnowienie przy starcie i odnowienie po odmowie to jedno żądanie,
   * a nie wyścig dwóch.
   */
  renewSession(): Observable<RenewedToken> {
    if (this.renewal$) {
      return this.renewal$;
    }

    // Bez tego nagłówka interceptor konwertujący nazwy pól zmieniłby `access_token`
    // na `accessToken`, odnowienie wyglądałoby na udane, a zapisany token byłby pusty.
    const headers = new HttpHeaders({'ignore-camel-case-interceptor': '1'});

    const renewal$ = this.httpClient
      .post<RenewedToken>(this.apiUrlService.getApiUrl() + SHOP_RENEWAL_PATH, {}, {headers})
      .pipe(shareReplay(1));
    this.renewal$ = renewal$;

    renewal$.subscribe({
      next: (renewed) => {
        this.storeSession(renewed.access_token, renewed.expires_at);
        this.renewal$ = null;
      },
      error: () => {
        this.renewal$ = null;
      },
    });

    return renewal$;
  }

  /**
   * Odnowienie przy starcie wykonuje w danej chwili tylko jedna karta. Ewidencja sesji po
   * stronie serwera pamięta dwa identyfikatory tokenu, więc okazany token wypada z pary przy
   * DRUGIM przesunięciu — kilka kart okazujących tę samą wartość daje odmowę tej trzeciej.
   * Warunek sprawdzamy ponownie po wejściu do blokady, bo karta czekająca na swoją kolej
   * okazałaby wartość odczytaną przed czekaniem.
   */
  private async renewUnderCrossTabLock(): Promise<void> {
    const locks = (navigator as unknown as {locks?: CrossTabLockManager}).locks;

    if (!locks) {
      await this.runRenewal();

      return;
    }

    await locks.request(AuthService.RENEWAL_LOCK, async () => {
      if (!this.localStorageService.getItem('token') || !this.isOlderThanHalfWindow()) {
        return;
      }

      await this.runRenewal();
    });
  }

  /**
   * Błąd odnowienia podjętego „na zapas" jest świadomie połykany: sesja wciąż obowiązuje,
   * a wyrzucanie klienta ze sklepu za nieudane przedłużenie byłoby gorsze niż pozwolenie mu
   * kupować do faktycznego wygaśnięcia. Wtedy zadziała ścieżka 401.
   */
  private runRenewal(): Promise<void> {
    return new Promise<void>(done => {
      this.renewSession().subscribe({
        next: () => done(),
        error: () => done(),
      });
    });
  }

  /**
   * Czy minęła połowa okresu ważności tokenu.
   *
   * Brak znacznika wydania przy obecnym terminie to sesja sprzed jego wprowadzenia —
   * odnawiamy, bo to jednocześnie przedłuża sesję i uzupełnia brakujący znacznik. Inaczej
   * taka sesja nie odnowiłaby się już nigdy.
   */
  private isOlderThanHalfWindow(): boolean {
    const issued = this.localStorageService.getItem('issuedAt');
    const expires = this.localStorageService.getItem('expiresAt');

    if (!expires) {
      return false;
    }

    if (!issued) {
      return true;
    }

    const issuedMs = new Date(issued).getTime();
    const expiresMs = new Date(expires).getTime();

    return Date.now() > issuedMs + (expiresMs - issuedMs) / 2;
  }

  /**
   * gets current user info
   * */
  private current(token: string): Observable<User> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient
      .get<UserInterface>(`${aliUrl}client/current`, {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + token,
        }),
      })
      .pipe(
        catchError((err) => {
          // Sesję rozbieramy WYŁĄCZNIE przy 401. Zadanie B5.5 kawałka B5: dotąd czyszczenie
          // szło przy KAŻDYM odrzuceniu, więc kod 500, timeout albo chwilowy brak łącza
          // wylogowywały kupującego z powodu, na który nie ma wpływu — choć ponowna próba
          // mogła się udać. Tylko 401 znaczy „to poświadczenie już nie działa"; pozostałe
          // kody mówią o serwerze albo o łączu i nie są informacją o sesji.
          //
          // Czyszczenie obejmuje magazyn ORAZ stan usługi: sam magazyn zostawiałby w stanie
          // token, którym nic się nie da zrobić, a interfejs dalej pokazywałby zalogowanego.
          if (err instanceof HttpErrorResponse && err.status === 401) {
            this.clearSession();
          }

          return of(null);
        }),
        map((user: UserInterface) => {
          if (user) {
            const _user: User = {
              ...user,
              get fullName(): string | undefined {
                return `${user.firstName} ${user.name}`;
              },
            };
            return _user;
          }
        }),
      );
  }

  private clearLoginStorage(): void {
    this.localStorageService.removeItem('token');
    this.localStorageService.removeItem('expiresAt');
  }
}
